import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '../../design-system';
import './UpgradeOfferSummary.css';

export type UpgradeOfferPlan = {
  planKey: string;
  label: string | null;
  amount: number;
  discountedAmount?: number | null;
  currency: string;
  interval: string | null;
  intervalCount: number | null;
  checkoutUrl: string;
};

export type UpgradeOfferPresentation = {
  requirements?: Array<{
    resource: string;
    used: number;
    currentLimit: number;
    requestedIncrement: number;
    requiredCapacity: number;
    proposedCapacity: number | null;
  }>;
  currentTier?: {
    productName: string;
    productSlug?: string | null;
    upgradeOrder?: number | null;
  } | null;
  productName?: string | null;
  checkoutUrl?: string | null;
  contactSalesUrl?: string | null;
  terminal?: boolean;
  resetDate?: string | null;
  monthlyAiRetailCreditUsd?: string | null;
  plans?: UpgradeOfferPlan[];
  capacity?: {
    monthlyAiRetailCreditUsd?: string | null;
    backtestMinutesPerMonth?: number | null;
    strategyRevisionsPerMonth?: number | null;
    backtestsPerMonth?: number | null;
    botLimit?: number | null;
  } | null;
};

function capacityLabel(
  value: number | null | undefined,
  singular: string,
  plural: string,
  monthly = false,
): string | null {
  if (value == null) return null;
  if (value < 0) return `Unlimited ${plural}`;
  return `${new Intl.NumberFormat('en-US').format(value)} ${value === 1 ? singular : plural}${monthly ? ' / month' : ''}`;
}

function effectiveAmount(plan: UpgradeOfferPlan): number {
  return plan.discountedAmount == null ? plan.amount : plan.discountedAmount;
}

/**
 * The checkout-matched price label for an offer ("$24.50/month"), or null
 * when Node's offer has no plan for its own checkout. Shared so compact
 * surfaces (the usage ring) show the same price as the full offer card.
 */
export function offerPriceLabel(
  offer: Pick<UpgradeOfferPresentation, 'checkoutUrl' | 'plans'> | null | undefined,
): string | null {
  if (!offer?.checkoutUrl || !Array.isArray(offer.plans)) return null;
  const plan = offer.plans.find((candidate) => candidate.checkoutUrl === offer.checkoutUrl);
  return plan ? formatPlanPrice(plan) : null;
}

function formatPlanPrice(plan: UpgradeOfferPlan): string {
  const currency = String(plan.currency || 'usd').toUpperCase();
  const amount = effectiveAmount(plan) / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
  const intervalCount = plan.intervalCount ?? 1;
  const interval = plan.interval || 'purchase';
  const cadence = intervalCount === 1 ? interval : `${intervalCount} ${interval}s`;
  return `${formatted}/${cadence}`;
}

export default function UpgradeOfferSummary({
  offer,
  compact = false,
  reason,
  onUpgrade,
  pending = false,
  secondaryAction,
  actionTestId,
  currentAiAllowanceUsd,
  hideCurrentPlan = false,
}: {
  offer: UpgradeOfferPresentation;
  compact?: boolean;
  reason?: string;
  onUpgrade?: () => void;
  pending?: boolean;
  secondaryAction?: React.ReactNode;
  actionTestId?: string;
  currentAiAllowanceUsd?: number;
  hideCurrentPlan?: boolean;
}): React.ReactElement | null {
  const plans = Array.isArray(offer.plans) ? offer.plans : [];
  const resourceNames = new Map([
    ['botLimit', 'Total bots'],
    ['alwaysOnBotLimit', 'Always-on or high-duty bots'],
    ['backtestsPerMonth', 'Monthly backtests'],
    ['backtestMinutesPerMonth', 'Monthly backtest minutes'],
    ['strategyRevisionsPerMonth', 'Monthly strategy revisions'],
    ['monthlyAiRetailCreditUsd', 'Monthly AI credits (USD)'],
  ]);
  // Free plan users never see their AI allowance in dollars (Rob, 2026-10-11):
  // they should just use it until it runs out.
  const onFreePlan = offer.currentTier?.upgradeOrder === 0;
  const requirements = (offer.requirements || []).filter(
    (item) =>
      resourceNames.has(item.resource) &&
      !(onFreePlan && item.resource === 'monthlyAiRetailCreditUsd'),
  );
  // Node owns the destination. Never show a price for a different interval.
  const plan = offer.checkoutUrl
    ? plans.find((candidate) => candidate.checkoutUrl === offer.checkoutUrl)
    : undefined;
  const capacity = offer.capacity;
  const allowance = capacity?.monthlyAiRetailCreditUsd ?? offer.monthlyAiRetailCreditUsd;
  const ratio =
    currentAiAllowanceUsd && currentAiAllowanceUsd > 0
      ? Number(allowance) / currentAiAllowanceUsd
      : 0;
  const aiBenefit =
    Number.isFinite(ratio) && ratio >= 1.1
      ? `About ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(ratio)}× monthly AI credits`
      : 'AI credits included every month';
  const benefits = [
    Number(allowance) > 0 ? aiBenefit : null,
    capacityLabel(capacity?.botLimit, 'running bot', 'running bots'),
    capacityLabel(capacity?.backtestsPerMonth, 'backtest', 'backtests', true),
    capacityLabel(capacity?.backtestMinutesPerMonth, 'backtest minute', 'backtest minutes', true),
    capacityLabel(
      capacity?.strategyRevisionsPerMonth,
      'strategy revision',
      'strategy revisions',
      true,
    ),
  ].filter((value): value is string => Boolean(value));
  const reset = offer.resetDate ? new Date(offer.resetDate) : null;
  const resetLabel =
    reset && Number.isFinite(reset.getTime())
      ? reset.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : null;

  return (
    <section
      className={`upgrade-offer-summary${compact ? ' upgrade-offer-summary--compact' : ''}`}
      aria-label="Plan upgrade"
    >
      <header className="upgrade-offer-summary__header">
        {!hideCurrentPlan && (
          <p className="upgrade-offer-summary__current">
            {offer.currentTier?.productName
              ? `Current plan: ${offer.currentTier.productName}`
              : 'Your plan upgrade'}
          </p>
        )}
        <h3 className="upgrade-offer-summary__title">
          {offer.terminal
            ? requirements.length
              ? 'This request needs more capacity'
              : 'You’re on our highest plan'
            : offer.productName
              ? `Upgrade to ${offer.productName}`
              : 'Choose your next plan'}
        </h3>
        {(reason || offer.terminal) && (
          <p className="upgrade-offer-summary__reason">
            {reason ||
              (offer.terminal
                ? requirements.length
                  ? 'No available self-serve plan covers this request. Manage existing usage or contact us.'
                  : 'There is no higher self-serve plan to upgrade to.'
                : 'Give your trading workflow more room to grow.')}
          </p>
        )}
      </header>
      {requirements.length > 0 && (
        <ul className="upgrade-offer-summary__benefits" aria-label="Capacity for your request">
          {requirements.map((item) => (
            <li key={item.resource}>
              {resourceNames.get(item.resource)}: {item.used} used /{' '}
              {item.currentLimit === -1 ? 'unlimited' : item.currentLimit} allowed. This request
              needs {item.requiredCapacity}.
              {item.proposedCapacity !== null &&
                ` Proposed plan: ${item.proposedCapacity === -1 ? 'unlimited' : item.proposedCapacity}.`}
            </li>
          ))}
        </ul>
      )}
      {!offer.terminal && benefits.length ? (
        <ul className="upgrade-offer-summary__benefits" aria-label="Included with this plan">
          {benefits.slice(0, 3).map((benefit) => (
            <li key={benefit}>
              <Check size={16} aria-hidden="true" />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {!offer.terminal && benefits.length > 3 ? (
        <details className="upgrade-offer-summary__details">
          <summary>All plan benefits</summary>
          <ul className="upgrade-offer-summary__benefits">
            {benefits.slice(3).map((benefit) => (
              <li key={benefit}>
                <Check size={16} aria-hidden="true" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {offer.terminal ? (
        <p className="upgrade-offer-summary__note">
          {resetLabel
            ? `Your monthly allowance resets ${resetLabel}.`
            : 'Check Usage for your allowance and reset date.'}{' '}
          You can keep using features with remaining capacity.
        </p>
      ) : plan ? (
        <footer className="upgrade-offer-summary__footer">
          <div className="upgrade-offer-summary__price">
            <span>New plan price</span>
            <strong>{formatPlanPrice(plan)}</strong>
          </div>
          {onUpgrade ? (
            <Button
              as="a"
              className="upgrade-offer-summary__action"
              href={plan.checkoutUrl}
              role="button"
              onClick={(event) => {
                event.preventDefault();
                onUpgrade();
              }}
              disabled={pending}
              data-testid={actionTestId}
            >
              {pending ? 'Opening checkout…' : `Upgrade to ${offer.productName}`}
              <ArrowRight size={16} aria-hidden="true" />
            </Button>
          ) : null}
          <p className="upgrade-offer-summary__note">
            Review total at checkout. Nothing is charged yet.
          </p>
        </footer>
      ) : (
        <p className="upgrade-offer-summary__note" role="status">
          Plan pricing is unavailable. Refresh to try again.
        </p>
      )}
      {secondaryAction ? (
        <div className="upgrade-offer-summary__secondary">{secondaryAction}</div>
      ) : null}
    </section>
  );
}
